import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Mic, Crown, FileText, Camera, ExternalLink, Sparkles } from "lucide-react";

/**
 * Public /voice page — Mural Social. No auth required.
 * Showcases KW Lead Growth League's content creators publicly:
 *   - Hall of Fame top 5 social_points (all-time)
 *   - Last 20 publications (clickable URLs to LinkedIn/Insta/YT/TikTok)
 *   - 30-day aggregate stats
 *
 * Acts as a recruitment / employer-brand tool for KW Lead.
 */
export default function Voice() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/voice/wall").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="voice-page">
      {/* Hero */}
      <div className="border-b border-[#222] py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Mic className="w-7 h-7 text-[#E50000]" />
            <div className="kw-overline text-[#E50000]">KW Lead · Mural Social</div>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black uppercase tracking-tighter leading-none">
            A voz da<br /><span className="text-[#E50000]">KW Lead</span>
          </h1>
          <p className="text-[#A3A3A3] mt-6 max-w-2xl text-lg">
            Conteúdo criado pelos nossos consultores em LinkedIn, Instagram, Facebook, TikTok e YouTube. Todos os meses elegemos a <strong className="text-white">Voice of KW</strong> — quem mais constrói marca pessoal.
          </p>
          {data && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8 max-w-2xl">
              <Stat icon={FileText} value={data.stats_30d.posts} label="Posts (últimos 30 dias)" />
              <Stat icon={Camera} value={data.stats_30d.videos} label="Vídeos humanizados (30d)" />
              <Stat icon={Sparkles} value={data.stats_30d.total_publications} label="Publicações partilhadas" />
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-12">
        {/* Hall of Fame */}
        {data && data.hall_of_fame.length > 0 && (
          <section data-testid="voice-hall-of-fame">
            <div className="flex items-center gap-2 mb-5">
              <Crown className="w-5 h-5 text-[#EAB308]" />
              <h2 className="font-display text-3xl font-black uppercase tracking-tight">Hall of Fame</h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-3">
              {data.hall_of_fame.map((p, idx) => (
                <div key={p.id} className="border border-[#222] p-4 text-center relative" data-testid={`hall-${p.id}`}>
                  {idx === 0 && <Crown className="w-4 h-4 text-[#EAB308] absolute top-2 right-2" />}
                  {p.photo_url ? (
                    <img src={p.photo_url} alt={p.name} className="w-16 h-16 rounded-full object-cover mx-auto mb-2 border-2 border-[#222]" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-2 font-bold text-[#737373]">
                      {(p.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="font-display font-black text-sm uppercase tracking-tight truncate">{p.name}</div>
                  <div className="text-[10px] text-[#737373] uppercase tracking-widest">{p.club || "—"}</div>
                  <div className="font-display text-2xl font-black text-[#E50000] mt-2">{Math.round(p.social_points)}</div>
                  <div className="text-[10px] text-[#737373] uppercase tracking-widest">pts sociais</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Latest Publications */}
        <section data-testid="voice-publications">
          <h2 className="font-display text-3xl font-black uppercase tracking-tight mb-5">Publicações recentes</h2>
          {!data ? (
            <p className="text-[#737373]">A carregar…</p>
          ) : data.publications.length === 0 ? (
            <p className="text-[#737373] italic">Ainda sem publicações partilhadas.</p>
          ) : (
            <div className="space-y-2">
              {data.publications.map((pub, i) => (
                <a key={pub.url || `${pub.author?.id || "anon"}-${i}`} href={pub.url} target="_blank" rel="noopener noreferrer"
                  className="block border border-[#222] hover:border-[#E50000] p-4 transition-colors flex items-center gap-4"
                  data-testid={`publication-${i}`}>
                  {pub.author.photo_url ? (
                    <img src={pub.author.photo_url} alt={pub.author.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-xs font-bold text-[#737373]">
                      {(pub.author.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{pub.author.name}</span>
                      <span className="text-[10px] uppercase tracking-widest text-[#737373]">{pub.author.club || ""}</span>
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border ${pub.kind === "video" ? "border-[#E50000] text-[#E50000]" : "border-[#3B82F6] text-[#3B82F6]"}`}>
                        {pub.kind === "video" ? "Vídeo" : "Post"}
                      </span>
                    </div>
                    <div className="text-xs text-[#737373] truncate mt-1">{pub.url}</div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-[#737373] flex-shrink-0" />
                </a>
              ))}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="border-t border-[#222] pt-8 text-center">
          <p className="text-[#A3A3A3]">És consultor KW Lead?</p>
          <a href="/login" className="kw-btn-primary inline-flex mt-3" data-testid="voice-login-cta">Entrar na liga →</a>
        </section>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="border border-[#222] p-4">
      <Icon className="w-4 h-4 text-[#E50000] mb-2" />
      <div className="font-display text-4xl font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">{label}</div>
    </div>
  );
}
